import { useState } from "react";
import { Button, Typography, Link, Tooltip } from "@mui/material";
import theme from "../../theme";

/*
EXPAND/COLLAPSE BUTTON COMPONENT
Button for showing more/less text in table cells
*/
export const ExpandButton = ({ isExpanded, onClick, isAlternateRow }) => (
  <Button
    onClick={onClick}
    sx={{
      cursor: "pointer",
      width: 36,
      height: 36,
      minWidth: 36,
      minHeight: 36,
      borderRadius: theme.borderRadius.border50,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isAlternateRow
        ? "transparent"
        : theme.customBackgrounds.lightBackground,
      transition: "all 0.3s ease",
    }}
  >
    <Typography
      sx={{
        color: "secondary.tiffanyBlue",
        fontSize: "22px",
        fontWeight: 300,
        lineHeight: 1,
        userSelect: "none",
      }}
    >
      {isExpanded ? "−" : "+"}
    </Typography>
  </Button>
);

/*
EXPANDABLE TEXT COMPONENT
Shows full text with expand button on small screens
*/
export const ExpandableText = ({
  text,
  maxLength,
  isExpanded,
  onToggle,
  isAlternateRow,
}) => {
  const shouldTruncate = text?.length > maxLength;
  const displayText = shouldTruncate
    ? text.slice(0, maxLength) + "..."
    : text || "-";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ width: "100%", textAlign: "left" }}>
        {isExpanded ? text : displayText}
      </span>
      {shouldTruncate && (
        <ExpandButton
          isExpanded={isExpanded}
          onClick={onToggle}
          isAlternateRow={isAlternateRow}
        />
      )}
    </div>
  );
};

/*
CUSTOM HOOK: useExpandableRows
Manages expand/collapse state for multiple table rows
*/
export const useExpandableRows = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const handleToggle = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };
  return { expandedRows, handleToggle };
};

/*
TABLE LINK COMPONENT
Renders a styled link with click tracking and proper href handling
*/
export const TableLink = ({
  href,
  text,
  clickedLinks,
  onLinkClick,
  fontWeight = 500,
  showTooltip = false,
  tooltipText = "",
  visitedColor,
  defaultColor = "secondary.tiffanyBlue",
  visitedDecoration,
}) => {
  const fullHref = href?.startsWith("http") ? href : `https://${href}`;

  const isClicked = clickedLinks?.has(fullHref);

  const finalColor = isClicked && visitedColor ? visitedColor : defaultColor;

  // Background colour use underline instead of changing colour
  const decorationForVisited = !isClicked
    ? "none"
    : visitedDecoration ?? (visitedColor ? "none" : "underline");

  const linkElement = (
    <Link
      href={fullHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onLinkClick(fullHref)}
      sx={{
        color: finalColor,
        textDecoration: decorationForVisited,
        fontWeight,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "inline-block",
        maxWidth: "100%",
        whiteSpace: showTooltip ? "nowrap" : "normal",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {text}
    </Link>
  );
  return showTooltip ? (
    <Tooltip title={tooltipText || text} arrow>
      {linkElement}
    </Tooltip>
  ) : (
    linkElement
  );
};
